import { supabase } from '@/lib/supabase';
import { creditWallet } from '@/lib/wallet';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Parrainage & revendeurs.
//
// Le superadmin crée des codes de parrainage rattachés à un type de
// partenaire :
//   • « revendeur »  : un compte qui revend les abonnements Zifek ;
//   • « structure »  : une organisation partenaire (association, ONG…) ;
//   • « commerce »   : n'importe quel commerce qui parraine des gens
//                      (il obtient alors des réductions sur son abonnement).
//
// Chaque code porte un pourcentage (commission versée au parrain à chaque
// paiement d'abonnement d'un filleul) et un nombre de mois gratuits offerts
// au filleul qui l'utilise à l'inscription.
// ─────────────────────────────────────────────────────────────

export type TypePartenaire = 'revendeur' | 'structure' | 'commerce';

export const TYPES_PARTENAIRE: { value: TypePartenaire; label: string; icon: string }[] = [
  { value: 'revendeur', label: 'Revendeur', icon: 'ri-store-2-line' },
  { value: 'structure', label: 'Structure partenaire', icon: 'ri-community-line' },
  { value: 'commerce', label: 'Commerce (parrainage)', icon: 'ri-building-2-line' },
];

export function typePartenaireLabel(type: string): string {
  return TYPES_PARTENAIRE.find((t) => t.value === type)?.label || 'Commerce';
}

export function typePartenaireIcon(type: string): string {
  return TYPES_PARTENAIRE.find((t) => t.value === type)?.icon || 'ri-share-circle-line';
}

export function normalizeTypePartenaire(value: unknown): TypePartenaire {
  const v = String(value || '').trim();
  return v === 'revendeur' || v === 'structure' ? v : 'commerce';
}

export interface CodeParrainage {
  id: number;
  code: string;
  typePartenaire: TypePartenaire;
  ownerId: number | null;
  partnerName: string;
  pourcentage: number;
  moisGratuits: number;
  actif: boolean;
  description: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CodeParrainageInput {
  code: string;
  typePartenaire: TypePartenaire;
  ownerId?: number | null;
  partnerName?: string;
  pourcentage?: number;
  moisGratuits?: number;
  actif?: boolean;
  description?: string;
}

export interface CommissionParrainage {
  id: number;
  code: string;
  codeId: number | null;
  parrainId: number | null;
  parrainName: string;
  filleulId: number | null;
  filleulName: string;
  plan: string;
  montant: number;
  pourcentage: number;
  commission: number;
  moisGratuits: number;
  statut: string;
  createdAt: string | null;
}

const CODE_COLUMNS = 'id, code, type_partenaire, owner_id, partner_name, pourcentage, mois_gratuits, actif, description, created_at, updated_at';

function mapCode(row: Record<string, unknown>): CodeParrainage {
  return {
    id: Number(row.id),
    code: (row.code as string) || '',
    typePartenaire: normalizeTypePartenaire(row.type_partenaire),
    ownerId: row.owner_id != null ? Number(row.owner_id) : null,
    partnerName: (row.partner_name as string) || '',
    pourcentage: Number(row.pourcentage) || 0,
    moisGratuits: Number(row.mois_gratuits) || 0,
    actif: row.actif !== false,
    description: (row.description as string) || '',
    createdAt: (row.created_at as string) || null,
    updatedAt: (row.updated_at as string) || null,
  };
}

/** Normalise un code (majuscules, sans espaces ni caractères spéciaux). */
export function normalizeCode(value: string): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 40);
}

/** Génère un code de parrainage lisible (préfixe + aléatoire). */
export function generateCode(prefix = 'ZIF'): string {
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  const rand2 = Math.random().toString(36).slice(2, 4).toUpperCase();
  return `${prefix}${rand}${rand2}`;
}

// ── CRUD (superadmin) ──────────────────────────────────────────

export async function listCodesParrainage(): Promise<CodeParrainage[]> {
  try {
    const { data, error } = await supabase
      .from('parrainage_codes')
      .select(CODE_COLUMNS)
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapCode);
  } catch {
    return [];
  }
}

export async function createCodeParrainage(input: CodeParrainageInput): Promise<void> {
  const code = normalizeCode(input.code);
  if (!code) throw new Error('Le code est obligatoire.');
  const { error } = await supabase.from('parrainage_codes').insert({
    code,
    type_partenaire: normalizeTypePartenaire(input.typePartenaire),
    owner_id: input.ownerId ?? null,
    partner_name: (input.partnerName || '').trim(),
    pourcentage: Number(input.pourcentage) || 0,
    mois_gratuits: Number(input.moisGratuits) || 0,
    actif: input.actif !== false,
    description: (input.description || '').trim(),
  });
  if (error) {
    if (/duplicate|unique/i.test(error.message)) throw new Error('Ce code existe déjà.');
    throw error;
  }
}

export async function updateCodeParrainage(id: number, patch: Partial<CodeParrainageInput>): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.code !== undefined) row.code = normalizeCode(patch.code);
  if (patch.typePartenaire !== undefined) row.type_partenaire = normalizeTypePartenaire(patch.typePartenaire);
  if (patch.ownerId !== undefined) row.owner_id = patch.ownerId ?? null;
  if (patch.partnerName !== undefined) row.partner_name = patch.partnerName.trim();
  if (patch.pourcentage !== undefined) row.pourcentage = Number(patch.pourcentage) || 0;
  if (patch.moisGratuits !== undefined) row.mois_gratuits = Number(patch.moisGratuits) || 0;
  if (patch.actif !== undefined) row.actif = patch.actif;
  if (patch.description !== undefined) row.description = patch.description.trim();
  const { error } = await supabase.from('parrainage_codes').update(row).eq('id', id);
  if (error) throw error;
}

export async function deleteCodeParrainage(id: number): Promise<void> {
  const { error } = await supabase.from('parrainage_codes').delete().eq('id', id);
  if (error) throw error;
}

/** Trouve un code actif par sa valeur (insensible à la casse). */
export async function findActiveCode(code: string): Promise<CodeParrainage | null> {
  const normalized = normalizeCode(code);
  if (!normalized) return null;
  try {
    const { data, error } = await supabase
      .from('parrainage_codes')
      .select(CODE_COLUMNS)
      .ilike('code', normalized)
      .eq('actif', true)
      .maybeSingle();
    if (error || !data) return null;
    return mapCode(data as Record<string, unknown>);
  } catch {
    return null;
  }
}

/** Codes rattachés à un compte (revendeur / commerce). */
export async function fetchCodesByOwner(ownerId: number): Promise<CodeParrainage[]> {
  if (!ownerId) return [];
  try {
    const { data, error } = await supabase
      .from('parrainage_codes')
      .select(CODE_COLUMNS)
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapCode);
  } catch {
    return [];
  }
}

/**
 * Garantit qu'un compte de type « Revendeur » possède bien un code de
 * parrainage, généré automatiquement à l'inscription. Renvoie le code.
 */
export async function ensureRevendeurCode(userId: number, typeName: string, partnerName?: string): Promise<string | null> {
  if (!userId) return null;
  const isRevendeur = /revendeur/i.test(typeName || '');
  if (!isRevendeur) return null;
  try {
    const existing = await fetchCodesByOwner(userId);
    if (existing.length > 0) return existing[0].code;
    // Génère un code unique.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const code = generateCode('REV');
      try {
        await createCodeParrainage({
          code,
          typePartenaire: 'revendeur',
          ownerId: userId,
          partnerName: partnerName || '',
          pourcentage: 10,
          moisGratuits: 0,
          actif: true,
          description: 'Code de revendeur (généré automatiquement)',
        });
        return code;
      } catch {
        /* collision, on réessaie */
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ── Commissions ────────────────────────────────────────────────

const COMMISSION_COLUMNS = 'id, code, code_id, parrain_id, parrain_name, filleul_id, filleul_name, plan, montant, pourcentage, commission, mois_gratuits, statut, created_at';

function mapCommission(row: Record<string, unknown>): CommissionParrainage {
  return {
    id: Number(row.id),
    code: (row.code as string) || '',
    codeId: row.code_id != null ? Number(row.code_id) : null,
    parrainId: row.parrain_id != null ? Number(row.parrain_id) : null,
    parrainName: (row.parrain_name as string) || '',
    filleulId: row.filleul_id != null ? Number(row.filleul_id) : null,
    filleulName: (row.filleul_name as string) || '',
    plan: (row.plan as string) || '',
    montant: Number(row.montant) || 0,
    pourcentage: Number(row.pourcentage) || 0,
    commission: Number(row.commission) || 0,
    moisGratuits: Number(row.mois_gratuits) || 0,
    statut: (row.statut as string) || 'creditee',
    createdAt: (row.created_at as string) || null,
  };
}

export async function listCommissions(limit = 200): Promise<CommissionParrainage[]> {
  try {
    const { data, error } = await supabase
      .from('parrainage_commissions')
      .select(COMMISSION_COLUMNS)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapCommission);
  } catch {
    return [];
  }
}

export async function listCommissionsForParrain(parrainId: number): Promise<CommissionParrainage[]> {
  if (!parrainId) return [];
  try {
    const { data, error } = await supabase
      .from('parrainage_commissions')
      .select(COMMISSION_COLUMNS)
      .eq('parrain_id', parrainId)
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapCommission);
  } catch {
    return [];
  }
}

/**
 * Enregistre la commission du parrain suite au paiement d'un abonnement
 * par un filleul, et crédite le portefeuille du parrain. Best-effort :
 * l'échec ne doit jamais bloquer l'abonnement.
 */
export async function recordReferralCommission(params: {
  filleulId: number;
  filleulName?: string;
  plan?: string;
  montant: number;
}): Promise<void> {
  const { filleulId, montant } = params;
  if (!filleulId || !Number.isFinite(montant) || montant <= 0) return;
  try {
    const { data: user } = await supabase
      .from('users')
      .select('id, code_parrainage, parrain_id')
      .eq('id', filleulId)
      .maybeSingle();
    const code = (user?.code_parrainage as string) || '';
    const parrainId = user?.parrain_id != null ? Number(user.parrain_id) : null;
    if (!code || !parrainId) return;

    const codeRow = await findActiveCode(code);
    if (!codeRow) return;

    const pourcentage = codeRow.pourcentage || 0;
    const commission = Math.round(montant * (pourcentage / 100) * 100) / 100;

    // Nom du parrain (pour l'historique).
    let parrainName = codeRow.partnerName;
    if (!parrainName) {
      const { data: parrain } = await supabase
        .from('users')
        .select('nomcommerce, name')
        .eq('id', parrainId)
        .maybeSingle();
      parrainName = (parrain?.nomcommerce as string) || (parrain?.name as string) || '';
    }

    const { error: insertError } = await supabase.from('parrainage_commissions').insert({
      code,
      code_id: codeRow.id,
      parrain_id: parrainId,
      parrain_name: parrainName,
      filleul_id: filleulId,
      filleul_name: params.filleulName || '',
      plan: params.plan || '',
      montant,
      pourcentage,
      commission,
      mois_gratuits: codeRow.moisGratuits,
      statut: 'creditee',
    });
    if (insertError) throw insertError;

    // Crédit du portefeuille du parrain (solde interne Zifek).
    if (commission > 0) {
      await creditWallet(
        parrainId,
        commission,
        'parrainage',
        `Commission parrainage — code ${code} (filleul ${params.filleulName || filleulId})`,
        code,
      );
    }
  } catch {
    /* best-effort */
  }
}